// src/ServicesPage.jsx
import React, { useState, useEffect, useRef } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { Loader, Settings, Truck, CheckCircle, Users, X } from 'lucide-react';
import { API } from './utils/database-frontend.js';

const ServicesPage = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  
  const featuresRef = useRef(null);
  const servicesRef = useRef(null);
  
  const isFeaturesInView = useInView(featuresRef, { once: true, threshold: 0.2 });
  const isServicesInView = useInView(servicesRef, { once: true, threshold: 0.1 });

  const features = [
    {
      icon: Truck,
      title: "Hızlı Teslimat",
      description: "7/24 acil teslimat hizmeti",
      count: "24/7",
      gradient: "from-brand-orange to-orange-500"
    },
    {
      icon: CheckCircle,
      title: "Kalite Güvencesi",
      description: "Sertifikalı hizmet kalitesi",
      count: "%100",
      gradient: "from-brand-navy to-blue-600"
    },
    {
      icon: Users,
      title: "Uzman Ekip",
      description: "Deneyimli profesyonel kadro",
      count: "15+",
      gradient: "from-brand-gray to-gray-600"
    },
    {
      icon: Settings,
      title: "Modern Teknoloji",
      description: "Son teknoloji ekipmanlar",
      count: "2024",
      gradient: "from-brand-orange to-red-500"
    }
  ];

  // Fetch services from database
  useEffect(() => {
    const fetchServices = async () => {
      try {
        setLoading(true);
        const fetchedServices = await API.services.getAll();
        console.log('Fetched services from API:', fetchedServices);
        setServices(fetchedServices);
        setError(null);
      } catch (err) {
        console.error('Error fetching services:', err);
        setError('Hizmetler yüklenirken bir hata oluştu. Lütfen daha sonra tekrar deneyin.');
        setServices([]);
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, []);

  const openModal = (service) => {
    setSelectedService(service);
  };

  const closeModal = () => {
    setSelectedService(null);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center bg-gradient-to-br from-brand-navy via-brand-gray to-brand-navy text-white overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-40"></div>
        
        {/* Animated Background Shapes */}
        <motion.div
          animate={{ 
            rotate: 360,
            scale: [1, 1.3, 1]
          }}
          transition={{ 
            duration: 30,
            repeat: Infinity,
            ease: "linear"
          }}
          className="absolute top-20 right-20 w-48 h-48 bg-brand-orange opacity-10 rounded-full"
        />
        <motion.div
          animate={{ 
            rotate: -360,
            scale: [1, 1.1, 1]
          }}
          transition={{ 
            duration: 20,
            repeat: Infinity,
            ease: "linear"
          }}
          className="absolute bottom-20 left-20 w-32 h-32 bg-white opacity-5 rounded-full"
        />

        <div className="relative z-10 text-center px-4 max-w-5xl">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="mb-8"
          >
            <div className="inline-flex items-center justify-center w-24 h-24 bg-brand-orange rounded-full mb-6">
              <Settings className="w-12 h-12 text-white" />
            </div>
          </motion.div>
          
          <motion.h1 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent"
          >
            Hizmetlerimiz
          </motion.h1>
          
          <motion.p 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.8 }}
            className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto leading-relaxed"
          >
            Profesyonel ekipman ve <span className="text-brand-orange font-semibold">20+ yıllık deneyim</span> 
            ile sunduğumuz kapsamlı hizmet çözümleri.
          </motion.p>
          
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.8 }}
            className="flex flex-wrap justify-center gap-4"
          >
            <div className="flex items-center bg-white bg-opacity-10 backdrop-blur-sm rounded-full px-6 py-3">
              <CheckCircle className="w-5 h-5 text-brand-orange mr-2" />
              <span className="text-white">7/24 Hizmet</span>
            </div>
            <div className="flex items-center bg-white bg-opacity-10 backdrop-blur-sm rounded-full px-6 py-3">
              <CheckCircle className="w-5 h-5 text-brand-orange mr-2" />
              <span className="text-white">Uzman Ekip</span>
            </div>
          </motion.div>
        </div>
      </section>



      {/* Services Section */}
      <section ref={servicesRef} className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={isServicesInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-brand-navy mb-4">Sunduğumuz Hizmetler</h2>
            <p className="text-xl text-brand-gray max-w-2xl mx-auto">
              İhtiyaçlarınıza özel, profesyonel hizmet çözümlerimiz
            </p>
          </motion.div>

          {/* Loading State */}
          {loading && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex flex-col items-center justify-center py-20"
            >
              <div className="relative">
                <Loader className="w-12 h-12 text-brand-orange animate-spin" />
                <div className="absolute inset-0 w-12 h-12 border-2 border-brand-orange border-t-transparent rounded-full animate-ping"></div>
              </div>
              <span className="mt-4 text-brand-gray text-lg font-medium">Hizmetler yükleniyor...</span>
              <p className="text-brand-gray text-sm mt-2">En güncel hizmet bilgilerimizi getiriyoruz</p>
            </motion.div>
          )}

          {/* Error State */}
          {error && (
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-20"
            >
              <div className="bg-gradient-to-r from-red-50 to-orange-50 border border-red-200 rounded-2xl p-8 max-w-md mx-auto">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <X className="w-8 h-8 text-red-500" />
                </div>
                <h3 className="text-lg font-semibold text-red-800 mb-2">Bir Hata Oluştu</h3>
                <p className="text-red-600">{error}</p>
              </div>
            </motion.div>
          )}

          {/* Services Grid */}
          {!loading && !error && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {services.length === 0 ? (
                <motion.div 
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="col-span-full text-center py-20"
                >
                  <div className="bg-gradient-to-br from-gray-100 to-gray-50 rounded-2xl p-12 max-w-md mx-auto">
                    <Settings className="w-16 h-16 text-brand-gray mx-auto mb-4 opacity-50" />
                    <h3 className="text-xl font-semibold text-brand-gray mb-2">Henüz Hizmet Yok</h3>
                  </div>
                </motion.div>
              ) : (
                services.map((service, index) => (
                  <motion.div
                    key={service.id}
                    initial={{ opacity: 0, y: 50, scale: 0.95 }}
                    animate={isServicesInView ? { opacity: 1, y: 0, scale: 1 } : {}}
                    transition={{ delay: index * 0.1, duration: 0.6 }}
                    whileHover={{ y: -5, scale: 1.01 }}
                    whileTap={{ scale: 0.98 }}
                    className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100 group cursor-pointer"
                    onClick={() => openModal(service)}
                  >
                    <div className="flex flex-col md:flex-row">
                      <div className="relative md:w-2/5 overflow-hidden">
                        <img 
                          src={service.imageUrl || service.image_url || 'https://placehold.co/500x300/f4712f/ffffff?text=Hizmet+Görseli'} 
                          alt={service.title}
                          className="w-full h-64 md:h-full object-contain bg-gradient-to-br from-gray-50 to-gray-100 group-hover:scale-105 transition-transform duration-500"
                                                  onError={(e) => {
                          e.target.src = 'https://placehold.co/500x300/f4712f/ffffff?text=Görsel+Bulunamadı';
                        }}
                      />

                      <div className="absolute top-4 right-4 bg-brand-navy text-white px-3 py-1 rounded-full text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        Detayları Gör
                      </div>
                      </div>
                      
                      <div className="md:w-3/5 p-8">
                        <h3 className="text-2xl font-bold text-brand-navy mb-4 group-hover:text-brand-orange transition-colors duration-300">
                          {service.title}
                        </h3>
                        
                        <p className="text-brand-gray leading-relaxed mb-6 break-words">
                          {service.description}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                ))
              )}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-brand-navy to-brand-gray text-white">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl font-bold mb-6">Hizmetlerimiz Hakkında Bilgi Alın</h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto text-gray-300">
              Size en uygun hizmet çözümlerini sunmak için uzman ekibimizle iletişime geçin.
            </p>
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <a 
                href="/contact"
                className="inline-block bg-brand-orange hover:bg-orange-600 text-white font-bold py-4 px-12 rounded-2xl transition duration-300 shadow-lg"
              >
                İletişime Geç
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Service Modal */}
      <AnimatePresence>
        {selectedService && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            onClick={closeModal}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              className="bg-white rounded-3xl max-w-4xl w-full max-h-[85vh] overflow-y-auto shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header with Image */}
              <div className="relative">
                <img 
                  src={selectedService.imageUrl || selectedService.image_url || 'https://placehold.co/500x300/f4712f/ffffff?text=Hizmet+Görseli'} 
                  alt={selectedService.title}
                  className="w-full h-80 object-contain bg-gradient-to-br from-gray-50 to-gray-100 rounded-t-3xl"
                  onError={(e) => {
                    e.target.src = 'https://placehold.co/500x300/f4712f/ffffff?text=Görsel+Bulunamadı';
                  }}
                />
                <button
                  onClick={closeModal}
                  className="absolute top-6 right-6 bg-white bg-opacity-90 backdrop-blur-sm rounded-full p-3 hover:bg-opacity-100 transition duration-300 shadow-lg"
                >
                  <X className="w-6 h-6 text-brand-gray" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-8">
                <h2 className="text-4xl font-bold text-brand-navy mb-6 leading-tight">
                  {selectedService.title}
                </h2>
                
                <div className="text-brand-gray text-lg leading-relaxed whitespace-pre-wrap break-words">
                  {selectedService.description}
                </div>
                
                {/* Close Button */}
                <div className="mt-8 flex justify-center">
                  <button 
                    onClick={closeModal}
                    className="bg-gradient-to-r from-brand-orange to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold py-4 px-12 rounded-2xl transition duration-300 transform hover:scale-105 shadow-lg"
                  >
                    Kapat
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ServicesPage;